import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { appConfig } from '@/config/app.config';
import { adminDashboardConfig } from '@/config/tenant.config';
import {
  isKnowledgeTenantRequest,
  resolveAdminTenantHeader,
  resolveKnowledgeTenantHeader,
} from '@/datasource/network/adminTenantHeader';
import { localSource } from '@/datasource/local/localSource';

type UnauthorizedHandler = () => void;

let onUnauthorized: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  onUnauthorized = handler;
}

export const backendService = axios.create({
  baseURL: appConfig.apiBaseUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

backendService.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localSource.getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.headers) {
      config.headers[adminDashboardConfig.gatewayHeader] = localSource.getTenantGatewayKey();
      config.headers[adminDashboardConfig.tenantHeader] = isKnowledgeTenantRequest(config.url)
        ? resolveKnowledgeTenantHeader()
        : resolveAdminTenantHeader();
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

backendService.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      localSource.clearAuth();
      if (onUnauthorized) {
        onUnauthorized();
      } else {
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }
    }
    return Promise.reject(error);
  },
);
